import {
  Pagination,
  SortAscendingIcon,
  SortDescendingIcon,
  Typography,
  useMediaQuery,
  useTheme,
} from '@forgeglobal/design-framework';
import {
  AccessorFn,
  CellContext,
  ColumnOrderState,
  createColumnHelper,
  ExpandedState,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  HeaderContext,
  Table as ReactTable,
  Row,
  useReactTable,
} from '@tanstack/react-table';
import Spinner from '@tcs/common/components/atoms/Spinner';
import cs from 'classnames';
import React, { ReactNode, useEffect, useState } from 'react';
import ExpandCell from './cells/ExpandCell';
import { useStyles } from './style';

type ColumnExtraProps = {
  accessor: string | ((row: unknown) => string);
  header: (props: HeaderContext<unknown, unknown>) => ReactNode;
  cell: (info: CellContext<unknown, unknown>) => ReactNode;
  size?: number;
  mobilePriority?: number;
  mobileTitle?: boolean;
  mobileMain?: boolean;
  id?: string;
  sortable?: boolean;
};

export interface TableProps {
  data: unknown[];
  columns: ColumnExtraProps[];
  loading?: boolean;
  customMobileLayout?: (
    table: ReactTable<unknown>,
    onRowClick?: (row: unknown) => void,
  ) => ReactNode;
  rowsPerPage?: number;
  page?: number;
  setPage?: (page: number) => void;
  totalItems?: number;
  onRowClick?: (row: unknown) => void;
  onColumnClick?: (columnId: string, sortDirection?: 'asc' | 'desc') => void;
  customRowCssFunction?: (row: unknown) => object;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
}

const DEFAULT_DATA = [];

const Table = ({
  data = DEFAULT_DATA,
  columns,
  loading,
  customMobileLayout,
  rowsPerPage = 10,
  page = 1,
  setPage,
  totalItems,
  onRowClick,
  onColumnClick,
  customRowCssFunction,
  defaultSortColumn,
  defaultSortDirection = 'asc',
  sortColumn,
  sortDirection: sortDirectionProp,
}: TableProps) => {
  const classes = useStyles({});
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>([]);
  const [expanded, setExpanded] = React.useState<ExpandedState>({});
  const [inMemoryPage, setInMemoryPage] = useState(page);
  const [inMemoryPaginatedRows, setInMemoryPaginatedRows] = useState(null);
  const isControlledPagination = typeof setPage === 'function';
  const totalItemsCount = totalItems || data.length;

  // Calculate max valid page for current data
  const maxPage = Math.max(1, Math.ceil(totalItemsCount / rowsPerPage));
  const currentPage = isControlledPagination
    ? Math.min(page, maxPage)
    : Math.min(inMemoryPage, maxPage);
  const columnHelper = createColumnHelper<unknown>();

  // Create a map of column IDs to their sortable status
  const sortableColumns = columns.reduce(
    (acc, column) => {
      const columnId =
        column.id ||
        (typeof column.accessor === 'string' ? column.accessor : '');
      acc[columnId] = column.sortable || false;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const computedColumns = columns.map((column) =>
    columnHelper.accessor(column.accessor as AccessorFn<unknown, unknown>, {
      header: column.header,
      cell: column.cell,
      size: column.size,
      id: column.id || (column.accessor as string),
    }),
  );

  const getDefaultSortField = () => {
    const columnIds = computedColumns.map((col) => col.id);
    return columnIds.includes(defaultSortColumn) ? defaultSortColumn : '';
  };

  // Internal state for uncontrolled mode
  const [internalSortByField, setInternalSortByField] = useState(
    getDefaultSortField(),
  );
  const [internalSortDirection, setInternalSortDirection] =
    useState(defaultSortDirection);

  // Determine if component is in controlled mode (parent provides sort props)
  const isControlled = sortColumn !== undefined;

  // Use controlled props if provided, otherwise use internal state
  const sortByField = isControlled ? sortColumn : internalSortByField;
  const sortDirection = isControlled
    ? sortDirectionProp || 'asc'
    : internalSortDirection;

  useEffect(() => {
    const resortedIds = columns
      .map((col) => ({
        ...col,
        mobilePriority: col.mobilePriority || 99,
      }))
      .sort((a, b) => a.mobilePriority - b.mobilePriority)
      .map((c) => c.accessor as string);
    setColumnOrder(isMobile ? resortedIds : []);
  }, [isMobile]);

  const table = useReactTable({
    data: inMemoryPaginatedRows || data,
    columns: computedColumns,
    getCoreRowModel: getCoreRowModel(),
    debugTable: true,
    onColumnOrderChange: setColumnOrder,
    state: {
      columnOrder,
      expanded,
    },
    onExpandedChange: setExpanded,
    getExpandedRowModel: getExpandedRowModel(),
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    getSubRows: (row) => row?.details,
  });

  const goToPage = (newPage: number) => {
    if (page !== newPage && setPage) {
      setPage(newPage);
    } else if (!setPage) {
      setInMemoryPaginatedRows(
        data.slice((newPage - 1) * rowsPerPage, newPage * rowsPerPage),
      );
      setInMemoryPage(newPage);
    }
  };

  useEffect(() => {
    setInMemoryPaginatedRows(
      data.length < rowsPerPage ? data : data.slice(0, rowsPerPage),
    );
    if (!isControlledPagination) {
      setInMemoryPage(1);
    }
  }, [data, rowsPerPage, isControlledPagination]);

  const shouldShowPagination = rowsPerPage < totalItemsCount;

  const noResultBlock = (
    <div className={classes.alternateContentBlock}>
      <Typography variant="body2" color="textSecondary">
        There is no data to display
      </Typography>
    </div>
  );

  const loadingBlock = (
    <div className={classes.alternateContentBlock}>
      <Spinner size="small" />
    </div>
  );

  const renderAlternateContentBlock = () => {
    if (loading) return loadingBlock;
    if (!data.length) return noResultBlock;
    return null;
  };
  const handleRowClick = (row: Row<unknown>) => () => {
    if (row.getCanExpand()) {
      return row.getToggleExpandedHandler()();
    }

    if (!onRowClick) return;
    onRowClick(row.original);
  };
  const handleColumnClick = (column: string) => () => {
    if (!onColumnClick) return;

    let newSortDirection: 'asc' | 'desc' = 'asc';

    if (sortByField === column) {
      newSortDirection = sortDirection === 'asc' ? 'desc' : 'asc';
    }

    // Only update internal state in uncontrolled mode
    if (!isControlled) {
      setInternalSortDirection(newSortDirection);
      setInternalSortByField(column);
    }

    // Always call the callback to notify parent
    onColumnClick(column, newSortDirection);
  };

  const defaultMobileLayout = !!data.length && (
    <div className={classes.cardTable}>
      {table.getRowModel().rows.map((row) => {
        return (
          <div className={classes.cardRowWrapper} key={row.id}>
            <div
              className={cs(classes.cardRow, {
                [classes.clickable]: onRowClick,
              })}
              onClick={handleRowClick(row)}
              style={customRowCssFunction ? customRowCssFunction(row) : {}}>
              {row.getVisibleCells().map((cell, index) => {
                return (
                  <div className={classes.cardCell} key={cell.id}>
                    <div className={classes.cardTitle}>
                      {flexRender(
                        cell.column.columnDef.header,
                        table.getHeaderGroups()[0].headers[index].getContext(),
                      )}
                    </div>
                    <div
                      className={classes.cardValue}
                      data-test-id={cell.column.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            {table.getCanSomeRowsExpand() && (
              <div className={classes.cardExpand}>
                <ExpandCell
                  isExpanded={row.getIsExpanded()}
                  canExpand={row.getCanExpand()}
                  onToggle={row.getToggleExpandedHandler()}
                />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  if (inMemoryPage !== page && setPage) {
    // reset page in memory when transitioning between search queries
    setInMemoryPage(page);
  }

  const mobileTable = customMobileLayout
    ? customMobileLayout(table, handleRowClick)
    : defaultMobileLayout;

  const desktopTable = (
    <table className={classes.table}>
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSortable = sortableColumns[header.id];
              return isSortable ? (
                <th
                  onClick={handleColumnClick(header.id)}
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: header.getSize(), cursor: 'pointer' }}>
                  <div className={classes.headerCell}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {sortByField === header.id && sortDirection === 'asc' && (
                      <SortAscendingIcon
                        fontSize="small"
                        className={classes.sortIcon}
                      />
                    )}
                    {sortByField === header.id && sortDirection === 'desc' && (
                      <SortDescendingIcon
                        fontSize="small"
                        className={classes.sortIcon}
                      />
                    )}
                  </div>
                </th>
              ) : (
                <th
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: header.getSize() }}>
                  <div className={classes.headerCell}>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                  </div>
                </th>
              );
            })}
            {table.getCanSomeRowsExpand() && (
              <th className={classes.tableExpand}> </th>
            )}
          </tr>
        ))}
      </thead>
      <tbody>
        {table.getRowModel().rows.map((row) => {
          return (
            <tr
              key={row.id}
              onClick={handleRowClick(row)}
              className={cs({
                [classes.clickable]: onRowClick,
              })}
              style={customRowCssFunction ? customRowCssFunction(row) : {}}>
              {row.getVisibleCells().map((cell) => {
                return (
                  <td
                    key={cell.id}
                    style={{ width: cell.column.getSize() }}
                    data-test-id={cell.column.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                );
              })}
              {table.getCanSomeRowsExpand() && (
                <td
                  className={classes.tableExpand}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}>
                  <ExpandCell
                    isExpanded={row.getIsExpanded()}
                    canExpand={row.getCanExpand()}
                    onToggle={row.getToggleExpandedHandler()}
                  />
                </td>
              )}
            </tr>
          );
        })}
      </tbody>
    </table>
  );

  return (
    <div className={classes.wrapper}>
      {isMobile ? mobileTable : desktopTable}
      {renderAlternateContentBlock()}
      {shouldShowPagination && (
        <Pagination
          totalItems={totalItemsCount}
          itemsPerPage={rowsPerPage}
          goToPage={goToPage}
          currentPage={currentPage}
        />
      )}
    </div>
  );
};

export default Table;
